import { useState } from 'react';
import { ExternalLink, LogIn, AlertTriangle, User, Search, ArrowLeft, Shield } from 'lucide-react';
import type { TenantAdmin, TenantDashboardAdmin, ImpersonateUserResponse } from '@yawi/types';
import { Modal, Button, Badge } from '@/core/ui/components';
import { useServices, useToast } from '@/app/providers';

interface ImpersonateModalProps {
  tenant: TenantAdmin;
  admins: TenantDashboardAdmin[];
  onClose: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  TENANT_ADMIN: 'Admin',
  TENANT_USER: 'Usuario',
  ADMIN: 'Admin',
  RESIDENT: 'Residente',
  GUARD: 'Guardia',
  MAINTENANCE: 'Mantenimiento',
  ACCOUNTANT: 'Contador',
};

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  ACTIVE: 'success',
  active: 'success',
  INVITED: 'warning',
  invited: 'warning',
  DISABLED: 'error',
  disabled: 'error',
};

type Step = 'select' | 'confirm' | 'success';

export function ImpersonateModal({ tenant, admins, onClose }: ImpersonateModalProps) {
  const { authRepository } = useServices();
  const { showToast } = useToast();

  const [step, setStep] = useState<Step>('select');
  const [selectedUser, setSelectedUser] = useState<TenantDashboardAdmin | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [impersonateData, setImpersonateData] = useState<ImpersonateUserResponse | null>(null);

  // Filter admins based on search query
  const filteredAdmins = admins.filter((admin) => {
    const query = searchQuery.toLowerCase();
    return (
      admin.name.toLowerCase().includes(query) ||
      admin.email.toLowerCase().includes(query) ||
      admin.role.toLowerCase().includes(query)
    );
  });

  const handleSelectUser = (admin: TenantDashboardAdmin) => {
    setSelectedUser(admin);
    setStep('confirm');
  };

  const handleBack = () => {
    setStep('select');
    setSelectedUser(null);
  };

  const handleImpersonate = async () => {
    if (!selectedUser) return;

    setIsLoading(true);
    try {
      const response = await authRepository.impersonateUser(tenant.id, selectedUser.id);
      setImpersonateData(response);
      setStep('success');
      showToast('success', 'Impersonación iniciada correctamente');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al impersonar usuario';
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenApp = () => {
    if (!impersonateData) return;

    // apps/web no tiene ningún código que lea un hand-off de impersonación
    // desde storage (ni localStorage ni sessionStorage) — auto-login en la
    // pestaña nueva no está implementado. Guardar accessToken/refreshToken
    // en localStorage acá no cumplía ninguna función real y sí dejaba un
    // refresh token en texto plano expuesto a XSS en el origen de
    // web-admin. Si en el futuro se implementa el auto-login, debe hacerse
    // con un código de intercambio de un solo uso y corta vida (mismo
    // patrón que el magic-link existente), nunca pasando el token real por
    // storage o por la URL.
    const tenantUrl = `https://${impersonateData.impersonating.tenantSlug}.hommi.mx`;
    window.open(tenantUrl, '_blank');
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={step === 'select' ? 'Seleccionar Usuario' : step === 'confirm' ? 'Confirmar Impersonación' : 'Impersonación Exitosa'}
      size="lg"
    >
      {/* Step 1: Select User */}
      {step === 'select' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, email o rol..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* User List */}
          <div className="max-h-80 overflow-y-auto space-y-2">
            {filteredAdmins.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                No se encontraron usuarios
              </p>
            ) : (
              filteredAdmins.map((admin) => {
                const statusColor = STATUS_COLORS[admin.status] || 'default';
                const isActive = admin.status === 'ACTIVE' || admin.status === 'active';

                return (
                  <button
                    key={admin.id}
                    onClick={() => handleSelectUser(admin)}
                    disabled={!isActive}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                      isActive
                        ? 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                        : 'border-gray-100 dark:border-gray-800 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center shrink-0">
                      <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {admin.name}
                        </p>
                        <Badge variant={statusColor} className="text-xs">
                          {admin.status}
                        </Badge>
                        {admin.twoFactorEnabled && (
                          <span title="2FA habilitado">
                            <Shield className="w-3 h-3 text-green-600" />
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {admin.email}
                      </p>
                    </div>
                    <div className="shrink-0">
                      <Badge variant="purple" className="text-xs">
                        {ROLE_LABELS[admin.role] || admin.role}
                      </Badge>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Confirm */}
      {step === 'confirm' && selectedUser && (
        <div className="space-y-6">
          {/* Warning */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Aviso Importante
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Estás a punto de impersonar a <strong>{selectedUser.name}</strong> en{' '}
                <strong>{tenant.name}</strong>. Todas las acciones realizadas serán
                registradas bajo tu cuenta de administrador.
              </p>
            </div>
          </div>

          {/* User Info */}
          <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Usuario a Impersonar
            </h4>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <User className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{selectedUser.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
                <Badge variant="purple" className="mt-1 text-xs">
                  {ROLE_LABELS[selectedUser.role] || selectedUser.role}
                </Badge>
              </div>
            </div>
          </div>

          {/* Tenant Info */}
          <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Tenant
            </h4>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Nombre</dt>
                <dd className="font-medium text-gray-800 dark:text-white/90 mt-1">
                  {tenant.name}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Slug</dt>
                <dd className="font-medium text-gray-800 dark:text-white/90 mt-1">
                  {tenant.slug}
                </dd>
              </div>
            </dl>
          </div>

          {/* Actions */}
          <div className="flex justify-between gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={handleBack}
              leftIcon={<ArrowLeft className="w-4 h-4" />}
            >
              Volver
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleImpersonate}
                isLoading={isLoading}
                leftIcon={<LogIn className="w-4 h-4" />}
              >
                Impersonar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Success */}
      {step === 'success' && impersonateData && (
        <div className="space-y-6">
          {/* Success Message */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center shrink-0">
              <LogIn className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                Impersonación Lista
              </p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                Tokens de impersonación generados correctamente. Ahora puedes acceder a la
                aplicación como <strong>{impersonateData.impersonating.memberName}</strong>.
              </p>
            </div>
          </div>

          {/* Impersonation Details */}
          <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Detalles de Impersonación
            </h4>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Usuario</dt>
                <dd className="font-medium text-gray-800 dark:text-white/90">
                  {impersonateData.impersonating.memberName}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Email</dt>
                <dd className="font-medium text-gray-800 dark:text-white/90">
                  {impersonateData.impersonating.memberEmail}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Rol</dt>
                <dd>
                  <Badge variant="purple">
                    {ROLE_LABELS[impersonateData.impersonating.memberRole] ||
                      impersonateData.impersonating.memberRole}
                  </Badge>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Tenant</dt>
                <dd className="font-medium text-gray-800 dark:text-white/90">
                  {impersonateData.impersonating.tenantName}
                </dd>
              </div>
            </dl>
          </div>

          {/* Admin Info */}
          <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-800/50 text-xs text-gray-600 dark:text-gray-400">
            <p>
              Impersonando desde: <strong>{impersonateData.originalAdmin.name}</strong> (
              {impersonateData.originalAdmin.email})
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
            <Button
              onClick={handleOpenApp}
              leftIcon={<ExternalLink className="w-4 h-4" />}
            >
              Abrir App
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
